import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { convertBrandingToManorTheme, isValidPayload, brandTheme, brandContent } from 'ctm-util-functions/lib/branding';
import { pageContext, logToNR } from 'ctm-util-functions';
import { getBrand, getBrandingServiceUrl, getEnv, getExperimentID } from '../store/selectors';
import { themes as localFiles } from '../themes/themes';
import { experimentIDEnum } from '../../../ctm-vertical-base/base/reducers/page';

const getBrandThemeLocalFile = (brandcode) => localFiles[brandcode];
const isLocalOrDev = (env) => (env === 'local' || env === 'dev');
const isRebrandExperiment = (experimentID, brandCode, vertical) => (
    experimentID === experimentIDEnum.CTM_REBRAND
    && brandCode === 'ctmrebrand'
    && vertical === 'car-insurance'
);

const ENV_SUBDOMAIN_MAP = {
    dev: { ctm: 'dev', ctmrebrand: 'dev', islt: 'wwwdev' },
    uat: { ctm: 'uat', ctmrebrand: 'uat', islt: 'wwwuat' }
};

export const getEnvSubdomain = (environment, brandCode) => {
    const envConfig = ENV_SUBDOMAIN_MAP[environment];
    if (!envConfig) return null;
    return envConfig[brandCode];
};

export const resolveLogoUrlForEnvironment = (headerLogoImageLink, { brandCode, environment }) => {
    if (!headerLogoImageLink || brandCode === 'choo') {
        return headerLogoImageLink;
    }

    const subdomain = getEnvSubdomain(environment, brandCode);
    if (!subdomain) return headerLogoImageLink;

    try {
        const url = new URL(headerLogoImageLink);

        if (url.hostname.startsWith('www.')) {
            // Replace "www." with subdomain
            url.hostname = url.hostname.replace(/^www\./, `${subdomain}.`);
        } else {
            // Inject subdomain at the start
            url.hostname = `${subdomain}.${url.hostname}`;
        }

        return url.toString();
    } catch (error) {
        return headerLogoImageLink;
    }
};

const updateLogoLinks = (data, brandCode, environment) => {
    ['car', 'home'].forEach((verticalKey) => {
        const link = data?.vertical?.[verticalKey]?.links?.header_logo_image_link;

        if (link) {
            // eslint-disable-next-line no-param-reassign
            data.vertical[verticalKey].links.header_logo_image_link = resolveLogoUrlForEnvironment(link, { brandCode, environment });
        }
    });
};

export const useBrandingService = () => {
    const dispatch = useDispatch();

    const [manorTheme, setManorTheme] = useState(null);
    const isBrandThemeSet = !!manorTheme;
    const [brandingServiceApiResponse, setBrandingServiceApiResponse] = useState(undefined);

    let brandCode = useSelector(getBrand);
    const environment = useSelector(getEnv);
    const brandingServiceUrl = useSelector(getBrandingServiceUrl);
    const vertical = pageContext.getVertical();
    const journeyId = useSelector((state) => state.getIn(['user', 'journey', 'transaction', 'journeyId']));
    const experimentID = useSelector(getExperimentID);

    if (brandCode === 'ctm' && experimentID === experimentIDEnum.CTM_REBRAND && vertical === 'car-insurance') {
        brandCode = 'ctmrebrand';
    }

    const switchTheme = (brandThemes) => {
        // Target the root element to set css vars
        const root = document.querySelector(':root');
        const theme = brandTheme(brandThemes);

        if (theme) {
            Object.keys(theme).forEach((key) => {
                const styleKey = `--${key.replace(/_/g, '-')}`;
                root.style.setProperty(styleKey, theme[key]);
            });
            setManorTheme(convertBrandingToManorTheme(theme));
        } else {
            logToNR('Branding Service no brand theme', 'ERROR', vertical);
            throw new Error('Error with brand themes during theme switch');
        }

        const content = brandContent(vertical, brandThemes);
        if (!content) {
            logToNR('Branding Service no brand content', 'ERROR');
            throw new Error(`No brand content found for the vertical ${vertical} for brand ${brandCode}`);
        }

        dispatch({
            type: 'SET_CONTENT',
            value: content
        });
    };

    // Log when switching to rebrand theme
    useEffect(() => {
        if (brandCode === 'ctmrebrand' && experimentID === experimentIDEnum.CTM_REBRAND && vertical === 'car-insurance') {
            logToNR(`Switching theme from ctm to CTM-rebrand. JourneyId: ${journeyId}`, 'INFO', vertical);
        }
    }, [experimentID, brandCode, vertical, journeyId]);

    useEffect(() => {
        if (!brandCode) return;

        const controller = new AbortController();

        if (!isBrandThemeSet
            || isLocalOrDev(environment)
            || isRebrandExperiment(experimentID, brandCode, vertical)) {
            axios
                .get(`${brandingServiceUrl}${brandCode}`, {
                    timeout: 1000,
                    signal: controller.signal,
                })
                .then((response) => {
                    const payload = response.data;
                    if (isValidPayload(vertical, payload)) {
                        updateLogoLinks(payload.data, brandCode, environment);
                        setBrandingServiceApiResponse(payload);
                    } else {
                        logToNR('Branding Service Payload invalid', 'WARN');
                        const brandThemeFile = getBrandThemeLocalFile(brandCode);
                        updateLogoLinks(brandThemeFile.data, brandCode, environment);
                        setBrandingServiceApiResponse(brandThemeFile);
                    }
                })
                .catch((error) => {
                    if (axios.isCancel(error)) return;
                    if (error.code === 'ECONNABORTED') {
                        logToNR('Branding Service call timeout', 'ERROR');
                    } else {
                        logToNR(`Branding Service call error: ${error.message}`, 'ERROR');
                    }

                    const brandThemeFile = getBrandThemeLocalFile(brandCode);
                    updateLogoLinks(brandThemeFile.data, brandCode, environment);
                    setBrandingServiceApiResponse(brandThemeFile);
                });
        }

        return () => controller.abort();
    }, [brandCode, brandingServiceUrl, isBrandThemeSet, environment, experimentID, vertical]);

    // Set the theme based on a brandcode and branding service response being available
    useEffect(() => {
        if (brandingServiceApiResponse) {
            switchTheme(brandingServiceApiResponse);
        }
    }, [brandingServiceApiResponse]);

    return { isBrandThemeSet, manorTheme };
};